import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Users as UsersIcon, UserCheck, Briefcase } from "lucide-react";
import { api, formatXAF, formatDate, formatNumber } from "@/lib/api";
import { useEntity } from "@/lib/entity";
import Modal, { ConfirmDialog } from "@/components/Modal";

const CONTRACTS = ["CDI", "CDD", "Stage", "Intérim", "Consultant"];
const ROLES_RH = ["Directeur", "Manager", "Comptable", "Caissier", "Gérant", "Pompiste", "Barman", "Restaurateur", "Sécurité", "Nettoyage", "Autre"];

export default function HR() {
  const { currentEntityId, entities } = useEntity();
  const [data, setData] = useState({ employees: [], total: 0, active: 0, payroll: 0 });
  const [showForm, setShowForm] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [form, setForm] = useState(initForm(currentEntityId));
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, [currentEntityId]);
  useEffect(() => { setForm(initForm(currentEntityId)); }, [currentEntityId]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/hr/${currentEntityId}`);
      setData(data);
    } finally { setLoading(false); }
  }

  async function submit(e) {
    e.preventDefault();
    await api.post("/hr", { ...form, salary: parseFloat(form.salary) });
    setShowForm(false);
    setForm(initForm(currentEntityId));
    load();
  }

  async function remove(id) {
    await api.delete(`/hr/${id}`);
    load();
  }

  const opEntities = entities.filter((e) => e.id !== "GROUPE");

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="space-y-6" data-testid="hr-page">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Effectif total" value={formatNumber(data.total)} icon={UsersIcon} />
        <Stat label="Actifs" value={formatNumber(data.active)} icon={UserCheck} accent="ok" />
        <Stat label="Masse salariale" value={formatXAF(data.payroll)} icon={Briefcase} accent="citron" highlight />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Personnel</h2>
          <p className="text-xs text-obsidian-400 mt-1">{data.employees.length} fiche(s)</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="hr-new-button">
          <Plus size={14} /> Nouvel employé
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-obsidian-400 border-b border-line">
              <Th>Nom</Th><Th>Entité</Th><Th>Poste</Th><Th>Contrat</Th><Th>Embauche</Th>
              <Th>Statut</Th><Th align="right">Salaire</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-8 text-center text-obsidian-500">Chargement…</td></tr>}
            {!loading && data.employees.length === 0 && (
              <tr><td colSpan={8} className="p-12 text-center text-obsidian-500">
                Aucun employé enregistré.<br/>
                <button onClick={() => setShowForm(true)} className="mt-3 text-citron hover:underline text-sm">+ Créer la première fiche</button>
              </td></tr>
            )}
            {data.employees.map((emp) => (
              <tr key={emp.id} className="border-b border-line/50 hover:bg-obsidian-850 transition-colors">
                <Td className="font-medium text-obsidian-100">{emp.full_name}</Td>
                <Td><span className="pill bg-obsidian-800">{emp.entity_id}</span></Td>
                <Td className="text-obsidian-300">{emp.role}</Td>
                <Td><span className="pill bg-citron/10 text-citron">{emp.contract_type}</span></Td>
                <Td><span className="font-mono text-xs">{formatDate(emp.hire_date)}</span></Td>
                <Td>
                  <span className={`pill ${emp.status === "actif" ? "bg-ok/15 text-ok" : emp.status === "conge" ? "bg-warn/15 text-warn" : "bg-obsidian-800 text-obsidian-400"}`}>
                    {emp.status}
                  </span>
                </Td>
                <Td align="right" className="font-mono tabular">{formatXAF(emp.salary)}</Td>
                <Td align="right">
                  <button onClick={() => setConfirmId(emp.id)} className="btn-ghost h-7 w-7 p-0 text-obsidian-500 hover:text-bad">
                    <Trash2 size={12} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title="Nouvel employé" subtitle="Ajouter une fiche personnel">
        <form onSubmit={submit} className="space-y-4" data-testid="hr-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label block">Nom complet</label>
              <input className="input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
            </div>
            <div>
              <label className="label block">Entité</label>
              <select className="input" value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} required>
                {opEntities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label block">Poste</label>
              <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} required>
                {ROLES_RH.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="label block">Contrat</label>
              <select className="input" value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })}>
                {CONTRACTS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label block">Date d'embauche</label>
              <input type="date" className="input" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} required />
            </div>
            <div>
              <label className="label block">Salaire (XAF)</label>
              <input type="number" min="0" step="1" className="input font-mono" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label block">Téléphone</label>
              <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label className="label block">Email</label>
              <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="label block">Statut</label>
            <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="actif">Actif</option>
              <option value="conge">En congé</option>
              <option value="inactif">Inactif</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Annuler</button>
            <button type="submit" className="btn-primary" data-testid="hr-submit">Enregistrer</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={() => remove(confirmId)}
        title="Supprimer cet employé ?"
        message="Cette action est irréversible et sera tracée."
        confirmLabel="Supprimer"
      />
    </motion.div>
  );
}

function initForm(entityId) {
  return {
    entity_id: entityId === "GROUPE" ? "ROPS" : entityId,
    full_name: "",
    role: "Manager",
    contract_type: "CDI",
    salary: "",
    hire_date: new Date().toISOString().slice(0, 10),
    phone: "",
    email: "",
    status: "actif",
  };
}

function Stat({ label, value, icon: Icon, accent, highlight }) {
  const colors = { ok: "text-ok", citron: "text-citron" };
  return (
    <div className={`card p-5 ${highlight ? "ring-1 ring-citron/30" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="overline">{label}</span>
        {Icon && <Icon size={14} className={colors[accent] || "text-obsidian-500"} />}
      </div>
      <div className={`font-mono text-2xl font-semibold tabular ${colors[accent] || "text-obsidian-100"}`}>
        {value}
      </div>
    </div>
  );
}

function Th({ children, align = "left" }) { return <th className={`overline px-4 py-3 text-${align}`}>{children}</th>; }
function Td({ children, align = "left", className = "" }) { return <td className={`px-4 py-3 text-${align} ${className}`}>{children}</td>; }
